import { ChipSlider } from '@saay/blocks';
import { formatLocalDate, formatToman, type LocalDate } from '@saay/formatting';
import { Button } from '@saay/shadcn/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@saay/shadcn/components/popover';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { AdminSolarDateField } from './admin-date-field.js';
import { datePresetForRange, dateRangeForPreset, type DatePreset } from './date-preset-filter.js';
import { MoneyField, TextField } from '../kit.js';

export interface FilterPillOption {
  readonly label: string;
  readonly value: string;
}

function PillShell({
  active,
  children,
  disabled = false,
  label,
  onClear,
  open,
  setOpen,
}: {
  readonly active: boolean;
  readonly children: ReactNode;
  readonly disabled?: boolean;
  readonly label: string;
  readonly onClear: () => void;
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
}) {
  return (
    <div
      className={
        active
          ? 'inline-flex shrink-0 items-center rounded-full bg-primary/10 text-primary'
          : 'inline-flex shrink-0 items-center rounded-full'
      }
    >
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger
          render={
            <Button
              className={
                active
                  ? 'h-9 rounded-full border-primary/20 bg-transparent px-3 text-sm text-primary hover:bg-primary/10'
                  : 'h-9 rounded-full px-3 text-sm'
              }
              disabled={disabled}
              type="button"
              variant="outline"
            />
          }
        >
          {label}
          <ChevronDownIcon aria-hidden="true" className="size-3.5" />
        </PopoverTrigger>
        {children}
      </Popover>
      {active ? (
        <Button
          aria-label={`پاک‌کردن فیلتر ${label.split(':')[0]}`}
          className="me-1 size-7 rounded-full text-primary hover:bg-primary/15"
          onClick={onClear}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          <XIcon aria-hidden="true" className="size-3.5" />
        </Button>
      ) : null}
    </div>
  );
}

function ChoiceRows({
  label,
  onChange,
  options,
  value,
}: {
  readonly label: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly FilterPillOption[];
  readonly value: string;
}) {
  return (
    <div aria-label={label} className="flex flex-col gap-1" role="radiogroup">
      {options.map((option) => {
        const checked = option.value === value;
        return (
          <button
            aria-checked={checked}
            className="flex min-h-9 w-full items-center justify-between rounded-xl px-3 py-2 text-start hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            key={option.value}
            onClick={() => onChange(option.value)}
            role="radio"
            type="button"
          >
            <span>{option.label}</span>
            {checked ? <CheckIcon aria-hidden="true" className="size-4 text-primary" /> : null}
          </button>
        );
      })}
    </div>
  );
}

/** A compact single-choice filter anchored to a stock shadcn popover. */
export function FilterPill({
  disabled = false,
  emptyLabel,
  inactiveLabel,
  label,
  onChange,
  options,
  value,
}: {
  readonly emptyLabel?: string;
  readonly disabled?: boolean;
  readonly inactiveLabel?: string;
  readonly label: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly FilterPillOption[];
  readonly value: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);
  const active = value !== '';
  const shownLabel = active ? `${label}: ${selected?.label ?? 'نامشخص'}` : (inactiveLabel ?? label);
  const choices =
    emptyLabel === undefined
      ? options
      : [{ label: emptyLabel, value: '' }, ...options.filter((option) => option.value !== '')];
  return (
    <PillShell
      active={active}
      disabled={disabled}
      label={shownLabel}
      onClear={() => onChange('')}
      open={open}
      setOpen={setOpen}
    >
      <PopoverContent align="start" className="glass w-64 rounded-2xl p-2 shadow-sm">
        <ChoiceRows
          label={`انتخاب ${label}`}
          onChange={(next) => {
            onChange(next);
            setOpen(false);
          }}
          options={choices}
          value={value}
        />
      </PopoverContent>
    </PillShell>
  );
}

const DATE_OPTIONS = [
  { label: 'امروز', value: 'today' },
  { label: '۷ روز اخیر', value: 'last-7-days' },
  { label: '۳۰ روز اخیر', value: 'last-30-days' },
  { label: 'این ماه', value: 'this-month' },
  { label: 'بازهٔ دلخواه', value: 'custom' },
] as const satisfies readonly FilterPillOption[];

function customDateLabel(from: LocalDate, to: LocalDate): string {
  if (from !== '' && to !== '') return `${formatLocalDate(from)} تا ${formatLocalDate(to)}`;
  if (from !== '') return `از ${formatLocalDate(from)}`;
  if (to !== '') return `تا ${formatLocalDate(to)}`;
  return 'بازهٔ دلخواه';
}

export function DateFilterPill({
  from,
  namePrefix,
  onApply,
  to,
}: {
  readonly from: LocalDate;
  readonly namePrefix: string;
  readonly onApply: (from: LocalDate, to: LocalDate) => void;
  readonly to: LocalDate;
}) {
  const [open, setOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<DatePreset>(() =>
    datePresetForRange(from, to),
  );
  const [draftFrom, setDraftFrom] = useState<LocalDate>(from);
  const [draftTo, setDraftTo] = useState<LocalDate>(to);
  const preset = datePresetForRange(from, to);
  const active = from !== '' || to !== '';
  const presetLabel = DATE_OPTIONS.find((option) => option.value === preset)?.label;
  const shownLabel = active
    ? `تاریخ: ${preset === 'custom' ? customDateLabel(from, to) : (presetLabel ?? '')}`
    : 'تاریخ';

  useEffect(() => {
    if (!open) return;
    setEditingPreset(preset);
    setDraftFrom(from);
    setDraftTo(to);
  }, [from, open, preset, to]);

  function choose(next: string) {
    const nextPreset = next as DatePreset;
    setEditingPreset(nextPreset);
    if (nextPreset === 'custom') return;
    const range = dateRangeForPreset(nextPreset);
    onApply(range.from, range.to);
    setOpen(false);
  }

  return (
    <PillShell
      active={active}
      label={shownLabel}
      onClear={() => onApply('', '')}
      open={open}
      setOpen={setOpen}
    >
      <PopoverContent align="start" className="glass w-80 rounded-2xl p-3 shadow-sm">
        <ChoiceRows
          label="انتخاب تاریخ"
          onChange={choose}
          options={DATE_OPTIONS}
          value={editingPreset}
        />
        {editingPreset === 'custom' ? (
          <div className="mt-2 flex flex-col gap-3 border-t border-border/60 pt-3">
            <div className="grid grid-cols-2 gap-2">
              <AdminSolarDateField
                label="از"
                name={`${namePrefix}-from`}
                onChange={(value) => setDraftFrom(value as LocalDate)}
                value={draftFrom}
              />
              <AdminSolarDateField
                label="تا"
                name={`${namePrefix}-to`}
                onChange={(value) => setDraftTo(value as LocalDate)}
                value={draftTo}
              />
            </div>
            <Button
              className="w-full rounded-full"
              onClick={() => {
                onApply(draftFrom, draftTo);
                setOpen(false);
              }}
              type="button"
            >
              اعمال
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </PillShell>
  );
}

function amountLabel(from: number | null, to: number | null): string {
  if (from !== null && to !== null) return `${formatToman(from)} تا ${formatToman(to)}`;
  if (from !== null) return `از ${formatToman(from)}`;
  if (to !== null) return `تا ${formatToman(to)}`;
  return '';
}

export function AmountFilterPill({
  from,
  onApply,
  to,
}: {
  readonly from: number | null;
  readonly onApply: (from: number | null, to: number | null) => void;
  readonly to: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState<number | null>(from);
  const [draftTo, setDraftTo] = useState<number | null>(to);
  const active = from !== null || to !== null;

  useEffect(() => {
    if (!open) return;
    setDraftFrom(from);
    setDraftTo(to);
  }, [from, open, to]);

  return (
    <PillShell
      active={active}
      label={active ? `مبلغ: ${amountLabel(from, to)}` : 'مبلغ'}
      onClear={() => onApply(null, null)}
      open={open}
      setOpen={setOpen}
    >
      <PopoverContent align="start" className="glass w-72 rounded-2xl p-3 shadow-sm">
        <div className="flex flex-col gap-3">
          <MoneyField label="از" onChangeRials={setDraftFrom} valueRials={draftFrom} />
          <MoneyField label="تا" onChangeRials={setDraftTo} valueRials={draftTo} />
          <Button
            className="w-full rounded-full"
            onClick={() => {
              onApply(draftFrom, draftTo);
              setOpen(false);
            }}
            type="button"
          >
            اعمال
          </Button>
        </div>
      </PopoverContent>
    </PillShell>
  );
}

/** A compact free-text filter for values such as minimum delinquent days. */
export function TextFilterPill({
  inputMode,
  label,
  onApply,
  placeholder,
  value,
}: {
  readonly inputMode?: 'decimal' | 'email' | 'numeric' | 'search' | 'tel' | 'text' | 'url';
  readonly label: string;
  readonly onApply: (value: string) => void;
  readonly placeholder?: string;
  readonly value: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const active = value.trim() !== '';

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  return (
    <PillShell
      active={active}
      label={active ? `${label}: ${value}` : label}
      onClear={() => onApply('')}
      open={open}
      setOpen={setOpen}
    >
      <PopoverContent align="start" className="glass w-72 rounded-2xl p-3 shadow-sm">
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            onApply(draft.trim());
            setOpen(false);
          }}
        >
          <TextField
            {...(inputMode === undefined ? {} : { inputMode })}
            label={label}
            onChange={(event) => setDraft(event.target.value)}
            {...(placeholder === undefined ? {} : { placeholder })}
            value={draft}
          />
          <Button className="w-full rounded-full" type="submit">
            اعمال
          </Button>
        </form>
      </PopoverContent>
    </PillShell>
  );
}

/** One compact filter line on every viewport; `ChipSlider` supplies mobile overflow. */
export function FilterPillBar({
  activeFilterCount,
  children,
  label,
  onClearAll,
  sort,
}: {
  readonly activeFilterCount: number;
  readonly children: ReactNode;
  readonly label: string;
  readonly onClearAll: () => void;
  readonly sort?: ReactNode;
}) {
  return (
    <div className="mb-3 flex min-w-0 items-center gap-2" role="toolbar" aria-label={label}>
      <ChipSlider className="min-w-0 flex-1" label={label}>
        {children}
        {activeFilterCount > 0 ? (
          <Button
            className="h-9 shrink-0 rounded-full px-3"
            onClick={onClearAll}
            type="button"
            variant="ghost"
          >
            پاک‌کردن همه
          </Button>
        ) : null}
        {sort}
      </ChipSlider>
    </div>
  );
}
